import React from 'react'
import { useAtomValue } from 'jotai'

import { ACCOUNT_DELETED_LIST_MODAL_SORT_FIELDS, IAccountDeletedSortField } from '../../hooks'
import * as state from '../../states'
import {
	IAccountAuthLocal,
	IAccount,
	IRole,
	Part,
	EAccountIsActive,
	ERoleAccess,
	ERoleExtraIsBuyer,
	EAccountIsFired
} from '../../types'
import {
	UiTable,
	IUiTableConfig,
	IconUploadCloud,
	headCellFormatterSortable,
	ESortOrder,
	UiDateInfo,
	RoleExtraIsBuyerIcon
} from '../ui'
import { IS_ACTIVE_COLORS_MAP, ACCOUNT_IS_FIRED_COLORS_MAP } from '../../constants'
import { Access } from '../../lib'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'
import { AccountIsActiveField, AccountIsFiredField } from './AccountListTable'


type IAccountDeletedListTableProps = {
	items: IAccount[];
	skip: number;
	sortBy: IAccountDeletedSortField;
	setSortBy: (sortBy: IAccountDeletedSortField) => void;
	order: ESortOrder;
	setOrder: (order: ESortOrder) => void;
	onResurrect?: (item: IAccount, e: React.MouseEvent) => void;
}

export const AccountDeletedListTable = (props: IAccountDeletedListTableProps) => {
	const {
		items = [],
		skip = 0,
		sortBy,
		setSortBy,
		order,
		setOrder,
		onResurrect
	} = props
	const { t } = useTranslation()
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)


	const accountDeletedListTableConfig: IUiTableConfig<IAccount>[] = [
		{
			key: 'id',
			title: t(ALL_LANG_KEY.account_list_table.column_id)
		},
		{
			key: 'email',
			title: t(ALL_LANG_KEY.account_list_table.column_email)
		},
		{
			key: 'auth.login',
			title: t(ALL_LANG_KEY.account_list_table.column_login),
			rowCellFormatter: ({ rowData }) => {
				const auth: Part<IAccountAuthLocal, 'updatedAt'> | null = rowData.auth ?? null
				const valueFormat: string = auth?.login ?? ''
				return valueFormat
			}
		},
		{
			key: 'extra.keitaroLogin',
			title: t(ALL_LANG_KEY.account_list_table.column_kt_group),
			rowCellFormatter: ({ rowData }) => {
				const extra = rowData.extra ?? null
				const valueFormat: string = extra?.keitaroLogin ?? ''
				return valueFormat
			}
		},
		{
			key: 'role.name',
			title: t(ALL_LANG_KEY.account_list_table.column_role),
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const role: Part<IRole, 'createdAt' | 'updatedAt'> | null = rowData.role ?? null
				const valueFormat: string = role ? `${role.name} (${role.code})` : '?'

				return valueFormat
			}
		},
		{
			key: 'role.extra.isBuyer',
			title: t(ALL_LANG_KEY.account_list_table.column_is_buyer),
			rowCellFormatter: ({ rowData }) => {
				const isBuyer = rowData.role?.extra.isBuyer
				return <div style={{ textAlign: 'center' }}>
					<RoleExtraIsBuyerIcon
						value={isBuyer ? ERoleExtraIsBuyer.Buyer : isBuyer === false ? ERoleExtraIsBuyer.NotBuyer : null}/>
				</div>
			}
		},
		{
			key: 'role.extra.inGroup',
			title: t(ALL_LANG_KEY.account_list_table.column_in_group),
			rowCellFormatter: ({ rowData }) => {
				const inGroup = rowData.role?.extra.inGroup ?? null
				return <div style={{ textAlign: 'center' }}>
					{inGroup}
				</div>
			}
		},
		{
			key: 'role.extra.isTeam',
			title: t(ALL_LANG_KEY.account_list_table.column_is_team),
			rowCellFormatter: ({ rowData }) => {
				const isTeam = rowData.role?.extra.isTeam ?? null
				return <div style={{ textAlign: 'center' }}>
					{isTeam}
				</div>
			}
		},
		{
			key: 'isActive',
			title: t(ALL_LANG_KEY.account_list_table.column_is_active),
			headCellFormatter: ({ value, configItem }) => {
				return <div style={{ textAlign: 'center' }}>{value}</div>
			},
			rowCellFormatter: ({ value, rowData, rowIndex, cellIndex }) => {
				const valueFormat: EAccountIsActive = value ? EAccountIsActive.active : EAccountIsActive.unactive

				return <div style={{ textAlign: 'center' }}
				>
					<AccountIsActiveField
						value={valueFormat}
						rowData={rowData}
						classNamesToAdd={IS_ACTIVE_COLORS_MAP?.[valueFormat]}
						onClick={(rowData, e) => {
							console.log('Click on Status field :::', { value, rowData, rowIndex, cellIndex })
						}}
					/>
				</div>
			}
		},
		{
			key: 'isFired',
			title: t(ALL_LANG_KEY.account_list_table.column_is_fired),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowIndex, cellIndex }) => {
				const valueFormat: EAccountIsFired = value ? EAccountIsFired.Fired : EAccountIsFired.NotFired

				return <>
					<AccountIsFiredField
						value={valueFormat}
						rowData={rowData}
						classNamesToAdd={ACCOUNT_IS_FIRED_COLORS_MAP?.[valueFormat]}
						onClick={(rowData, e) => {
							console.log('Click on Fired field :::', { value, rowData, rowIndex, cellIndex })
						}}
					/>
				</>
			}
		},
		{
			key: 'registeredAt',
			title: t(ALL_LANG_KEY.account_list_table.column_date_registered),
			headCellFormatter: ({ value, configItem, cellIndex }) => {
				return <div style={{ textAlign: 'center' }}>{value}</div>
			},
			rowCellFormatter: ({ value, rowData, rowIndex, cellIndex }) => {
				return (
					<UiDateInfo value={value} styles={{ textAlign: 'center' }}/>
				)
			}
		},
		{
			key: 'deletedAt',
			title: t(ALL_LANG_KEY.account_list_table.column_date_deleted),
			headCellFormatter: ({ value, configItem, cellIndex }) => {
				return <div style={{ textAlign: 'center' }}>{value}</div>
			},
			rowCellFormatter: ({ value, rowData, rowIndex, cellIndex }) => {
				return (
					<UiDateInfo value={value} styles={{ textAlign: 'center' }}/>
				)
			}
		},
		{
			key: 'ACTIONS',
			title: 'Actions',
			headCellFormatter: ({ configItem }) => {
				return <div>{''}</div>
			},
			rowCellFormatter: ({ rowData }) => {
				return <>
					<div
						style={{
							margin: '0 auto',
							textAlign: 'center'
						}}
					>

						{
							!!onResurrect && !!$access.$(ERoleAccess.AccountResurrect)
								? (
									<button name="edit"
									        className="icon border-0 rounded-circle text-center edit bg-success-transparent edit-item"
									        onClick={(e) => onResurrect && onResurrect(rowData, e)}
									>
										<IconUploadCloud/>
									</button>
								)
								: null
						}
					</div>
				</>
			}
		}
	]

	const accountDeletedListTableConfigSortable: IUiTableConfig<IAccount>[] = accountDeletedListTableConfig.map(
		(configItem) => {
			const configItemIsSortable = ACCOUNT_DELETED_LIST_MODAL_SORT_FIELDS.includes(configItem.key as IAccountDeletedSortField)

			if (!configItemIsSortable) {
				return configItem
			} else {
				return {
					...configItem,
					isSortable: true,
					headCellFormatter: ({
						value,
						configItem,
						cellIndex
					}) => {

						return headCellFormatterSortable({
							value,
							configItem,
							cellIndex,
							sortBy,
							setSortBy,
							order,
							setOrder
						})
					}
				}
			}
		})

	//console.log('AccountDeletedListTable:: ', { accountDeletedListTableConfig, accountDeletedListTableConfigSortable })

	return <>
		<UiTable
			data={items}
			config={accountDeletedListTableConfigSortable}
			// TODO: uncomment if necessary
			// classesToAdd={{
			// 	wrapper: 'table-sticky-wrapper',
			// }}
		/>
	</>
}